import Header from '../Header/Header';
import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom';
import { Formik, Form, ErrorMessage, setIn } from 'formik';
import { MachinesFormSchema } from "./schema"
import { Loader, SuccessModal } from '@ohif/ui';
import { _commonUrls, _machineUrls } from '@custom/Utils/constants/apiUrls';
import { customStyle } from '@custom/Utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import { machineApi } from '@custom/rtk-apis/machine/addMachine';
import { editMachineApi } from '@custom/rtk-apis/machine/editMachine';
import { getDeviceName } from '@custom/rtk-apis/machine/getDevice';
import { useTheme } from "../../ui/src/contextProviders";

const AddEditMachines = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { machinePayload } = useSelector((state: RootState) => state.getDeviceNameSliceResponse);
  const [modal, setModal] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [title, setTitle] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [load, setLoad] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [initialValues, setInitialValues] = useState({
    _id: '',
    machineName: '',
    machineType: '',
    aeTitle: "",
    ipAddress: "",
    port: "",
  })

  useEffect(() => {
    setLoad(true);
    setTimeout(() => {
      setLoad(false);
    }, 500);
  }, []);

  const machineTypes = [
    { label: 'Computed Radiography', value: 'CR' },
    { label: 'Digital Radiography', value: 'DR' },
    { label: 'Magnetic Resonance Imaging', value: 'MR' },
    { label: 'Ultrasound', value: 'US' },
    { label: 'Positron Emission Tomography', value: 'PET' },
    { label: 'Computed Tomography', value: 'CT' },
    { label: 'Nuclear Medicine', value: 'NM' },
    { label: 'Mammography', value: 'MG' },
    { label: 'Fluoroscopy', value: 'RF' },
    { label: 'X-ray Angiography', value: 'XA' },
    { label: 'Endoscopy', value: 'ES' },
    { label: 'Ophthalmology', value: 'OP' },
    { label: 'Radiotherapy', value: 'RT' },
    { label: 'Secondary Capture', value: 'SC' },
    { label: 'Intraoral Radiography', value: 'IO' },
    { label: 'Panoramic X-ray', value: 'PX' }
  ];

  useEffect(() => {
    if (location?.state?.editMode && location?.state?.machinesData.machineName) {
      dispatch(getDeviceName.getDeviceNameSlice({ deviceName: location?.state?.machinesData.machineName }));
    }
  }, [location?.state?.editMode]);


  useEffect(() => {
    if (machinePayload?.length > 0) {
      setInitialValues({
        _id: location?.state?.machinesData?._id || '',
        machineName: location?.state?.machinesData?.machineName || '',
        machineType: machinePayload[0]?.dicomPrimaryDeviceType?.[0] || '',
        aeTitle: location?.state?.machinesData?.aeTitle || '',
        ipAddress: location?.state?.machinesData?.ipAddress || '',
        port: location?.state?.machinesData?.port || '',
      })
    }
  }, [machinePayload, location?.state?.machinesData]);

  const handleSave = async (formValues) => {
    let formData = {
      dicomNetworkConnection: [
        {
          cn: formValues.machineName,
          dicomHostname: formValues.ipAddress,
          dicomPort: Number(formValues.port),
        },
      ],
      dicomNetworkAE: [
        {
          dicomNetworkConnectionReference: ["/dicomNetworkConnection/0"],
          dicomAETitle: formValues.aeTitle,
          dicomAssociationInitiator: true,
          dicomAssociationAcceptor: true,
        },
      ],
      dicomDeviceName: formValues.machineName,
      dicomPrimaryDeviceType: [formValues.machineType],
      dicomInstalled: true,
    };

    try {
      const response = location?.state?.editMode
        ? await dispatch(editMachineApi.editMachineApi({
          deviceName: formValues.machineName,
          savedInformation: formData,
        })).unwrap()
        : await dispatch(machineApi.fetchMachineApi({
          deviceName: formValues.machineName,
          savedInformation: formData,
        })).unwrap();

      if ('status' in response && (response.status === 204 || response.status === 200)) {
        setModal(true);
        setTitle(location?.state?.editMode ? 'Machine updated successfully.' : 'Machine saved successfully.');
        setTimeout(() => {
          setModal(false);
          navigate("/machines");
        }, 500);
      }
    } catch (error) {
      console.log('Error:', error);

      if (error?.status === 409) {
        setValidationError('Machine Name/AE Title already exists.');
      } else {
        setErrorMessage('An unexpected error occurred.');
        setErrorModal(true);
      }
    }
  };

  const cancel = () => {
    navigate("/machines");
  }

  const isDarkMode = useTheme();
  return (
    <>
      <div className='page page__full' style={
        isDarkMode?.isDarkMode ? { background: '#090c29' } : customStyle.mainBackground
      }>
        <Header isBack={true} title={location?.state?.editMode ? 'Edit Machine' : 'Add Machine'}></Header>
        <div className='page__body'>
          <div className='details'>

            <div className='form form--innersec'>
              <section className='form__section'>
                <h1 className='form__section__title'>Machine Details</h1>
                <Formik
                  initialValues={initialValues}
                  validationSchema={MachinesFormSchema}
                  enableReinitialize={true}
                  onSubmit={handleSave}
                >
                  {({
                    values,
                    handleChange,
                    handleBlur,
                  }) => {
                    return (

                      <Form>
                        <div className='formRow formRow__2'>
                          <div className='formCol'>
                            <div className='formRow formRow__2'>
                              <div className='formCol'>
                                <div className="form__Field">
                                  <label className="form__label" htmlFor='name'>
                                    Machine Name
                                    <span className="required-asterisk">*</span>
                                  </label>
                                  <input className="form__input form__input--sm"
                                    type="text"
                                    id="machineName"
                                    name="machineName"
                                    placeholder="Machine Name"
                                    disabled={location?.state?.editMode}
                                    onChange={handleChange}
                                    value={values.machineName}
                                    autoComplete='off'
                                  />
                                </div>
                                <ErrorMessage name="machineName" component="div" className="error text-error" />
                              </div>
                              <div className='formCol'>
                                <div className="form__Field">
                                  <label className='form__label' htmlFor='aeTitle'>
                                    AE Title
                                    <span className="required-asterisk">*</span>
                                  </label>
                                  <input
                                    className="form__input form__input--sm"
                                    type="text"
                                    id="aeTitle"
                                    name="aeTitle"
                                    placeholder="AE Title"
                                    onChange={handleChange}
                                    value={values?.aeTitle}
                                    autoComplete='off'
                                  />
                                  <ErrorMessage name="aeTitle" component="div" className="error text-error" />
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className='formCol'>
                            <div className='formRow formRow__2'>
                              <div className='formCol'>
                                <div className="form__Field">
                                  <label className="form__label" htmlFor='employeeId' >
                                    IP Address<span className="required-asterisk">*</span>
                                  </label>
                                  <input
                                    className="form__input form__input--sm"
                                    type="text"
                                    id="ipAddress"
                                    name="ipAddress"
                                    placeholder="IP Address"
                                    onChange={handleChange}
                                    value={values.ipAddress}
                                    autoComplete='off'
                                  />
                                  <ErrorMessage name="ipAddress" component="div" className="error text-error" />
                                </div>
                              </div>
                              <div className='formCol'>
                                <div className="form__Field">
                                  <label className='form__label' htmlFor='port'>
                                    Port
                                    <span className="required-asterisk">*</span>
                                  </label>
                                  <input
                                    className="form__input form__input--sm"
                                    type="text"
                                    id="port"
                                    name="port"
                                    placeholder="Port"
                                    maxLength={5}
                                    onChange={handleChange}
                                    value={values.port}
                                    autoComplete='off'
                                  />
                                  <ErrorMessage name="port" component="div" className="error text-error" />

                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='formRow formRow__2'>
                          <div className='formCol'>
                            <div className='formRow formRow__2'>
                              <div className='formCol'>
                                <div className='form__Field'>
                                  <label className='form__label' htmlFor='machineType' >Machine Type <span className="required-asterisk">*</span></label>
                                  <select
                                    id="machineType"
                                    name="machineType"
                                    className="form__select form__select--sm"
                                    style={customStyle.select}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values?.machineType}
                                  >
                                    <option value="">-Select-</option>
                                    {machineTypes.map((option: any) => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                  <ErrorMessage name="machineType" component="div" className="error text-error" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='btn__group'>
                          <button
                            className='btn btn__white'
                            onClick={cancel}
                          >
                            Cancel
                          </button>
                          <button
                            className='btn btn__primary'
                            type="submit"
                          >
                            {location?.state?.editMode ? "Update" : "Save"}
                          </button>
                        </div>
                      </Form>
                    )
                  }}
                </Formik>
              </section>
            </div>
          </div>
        </div>
        <SuccessModal
          isSuccessModalOpen={modal}
          title={title}
          handleCloseModal={() => setModal(false)}
        />
        <Loader isLoaderOpen={load}></Loader>
        {errorModal && (
          <div className="error-modal">
            <div className="error-modal-content">
              <h2 className='error-text'>Error!</h2>
              <p>{errorMessage}</p>
              <div className='close-btn'>
                <button className='btn btn__primary' onClick={() => setErrorModal(false)}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default AddEditMachines;
